/**
 * Logging wrapper: consoles locally and forwards to the error reporter.
 *
 * This is the only production file allowed to call `console.*` (enforced by
 * the `no-console` lint rule). Signatures mirror the console methods so call
 * sites migrate mechanically: `console.error(...)` -> `log.error(...)`.
 *
 * - `debug`/`info`: console only.
 * - `warn`: console + reporter breadcrumb.
 * - `error`: console + reporter event (captureException when an Error is
 *   among the args, message event otherwise).
 */

import { addTelemetryBreadcrumb, reportError, reportMessage } from './telemetry';

const isTestEnvironment = process.env.NODE_ENV === 'test';

function describe(args: unknown[]): string {
  return args
    .map((arg) => {
      if (typeof arg === 'string') return arg;
      if (arg instanceof Error) return arg.message;
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(' ')
    .slice(0, 500);
}

export const log = {
  debug(...args: unknown[]): void {
    if (!isTestEnvironment) console.debug(...args);
  },

  info(...args: unknown[]): void {
    if (!isTestEnvironment) console.info(...args);
  },

  warn(...args: unknown[]): void {
    if (!isTestEnvironment) console.warn(...args);
    addTelemetryBreadcrumb('log', describe(args), 'warning');
  },

  error(...args: unknown[]): void {
    if (!isTestEnvironment) console.error(...args);
    const error = args.find((arg) => arg instanceof Error);
    const message = describe(args);
    if (error) {
      reportError(error, { message });
    } else {
      reportMessage(message, 'error');
    }
  },
};
